<script setup>
import { nextTick, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';

import Button from 'next/button/Button.vue';
import Dialog from 'next/dialog/Dialog.vue';
import TestToolForm from './TestToolForm.vue';
import TestToolResult from './TestToolResult.vue';

const props = defineProps({
  serverId: {
    type: Number,
    required: true,
  },
});

const emit = defineEmits(['close']);
const { t } = useI18n();
const store = useStore();

const isOpen = ref(false);
const dialogRef = ref(null);
const formRef = ref(null);
const tool = ref({});
const testResult = ref(null);
const testHasError = ref(false);

const resetStates = () => {
  testResult.value = null;
  testHasError.value = false;
};

const handleTest = async () => {
  resetStates();

  if (!formRef.value) return;
  const { state, validate } = formRef.value;

  try {
    const isFormValid = await validate();
    if (!isFormValid) return;

    testResult.value = await store.dispatch('wgptMcpServers/testTool', {
      serverId: props.serverId,
      tool_name: tool.value.name,
      arguments: state,
    });
  } catch (error) {
    testHasError.value = true;
    testResult.value =
      error?.message ||
      t('WGPT_MCP_SERVERS.EDIT.TOOLS.TEST_TOOL.API.ERROR_MESSAGE');
  }
};

const handleDismiss = () => {
  isOpen.value = false;
  emit('close');
  resetStates();
};

const dismiss = () => {
  dialogRef.value.close();
  handleDismiss();
};

const open = toolData => {
  tool.value = toolData;
  isOpen.value = true;
  nextTick(() => {
    dialogRef.value?.open();
  });
};

defineExpose({ open, dismiss });
</script>

<template>
  <Dialog
    ref="dialogRef"
    type="edit"
    :title="t('WGPT_MCP_SERVERS.EDIT.TOOLS.TEST_TOOL.TITLE')"
    :description="t('WGPT_MCP_SERVERS.EDIT.TOOLS.TEST_TOOL.DESCRIPTION')"
    width="3xl"
    :show-confirm-button="false"
    :show-cancel-button="false"
    overflow-y-auto
    @close="handleDismiss"
  >
    <div v-if="isOpen" class="flex items-stretch gap-4">
      <div class="flex-1">
        <TestToolForm
          ref="formRef"
          :parameter-fields="tool.trigger?.input_mapping?.parameters || []"
        />
      </div>
      <div class="flex-1 overflow-hidden">
        <TestToolResult
          :result="testResult"
          :has-error="testHasError"
          @test="handleTest"
        />
      </div>
    </div>
    <div class="flex justify-end">
      <Button
        size="sm"
        color="blue"
        :label="t('WGPT_MCP_SERVERS.EDIT.TOOLS.TEST_TOOL.FORM.CANCEL')"
        variant="ghost"
        @click.prevent="dismiss"
      />
    </div>
  </Dialog>
</template>
